import React from "react";
import { Upload, Download, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { axiosPrivate } from "@/api/axios";
import API_ROUTES from "@/api/apiRoutes";

interface ParsedFact {
  title: string;
  description: string;
  type: string;
}

interface RowError {
  row: number;
  message: string;
}

const SAMPLE_CSV = `title,description,type
Drink more water,Staying hydrated improves digestion and energy levels throughout the day.,gut
Eat your greens,Leafy greens are packed with vitamins and minerals essential for gut health.,nutrition`;

function parseCSV(text: string): { rows: ParsedFact[]; errors: RowError[] } {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { rows: [], errors: [{ row: 0, message: "File is empty or missing header row." }] };

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const titleIdx = headers.indexOf("title");
  const descIdx = headers.indexOf("description");
  const typeIdx = headers.indexOf("type");

  const missing = [];
  if (titleIdx === -1) missing.push("title");
  if (descIdx === -1) missing.push("description");
  if (typeIdx === -1) missing.push("type");
  if (missing.length > 0) {
    return { rows: [], errors: [{ row: 0, message: `Missing required columns: ${missing.join(", ")}` }] };
  }

  const rows: ParsedFact[] = [];
  const errors: RowError[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Basic CSV split (handles quoted fields)
    const cols: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === "," && !inQuotes) { cols.push(cur.trim()); cur = ""; }
      else { cur += ch; }
    }
    cols.push(cur.trim());

    const title = cols[titleIdx]?.replace(/^"|"$/g, "") ?? "";
    const desc = cols[descIdx]?.replace(/^"|"$/g, "") ?? "";
    const type = cols[typeIdx]?.replace(/^"|"$/g, "") ?? "";

    if (!title || !desc || !type) {
      errors.push({ row: i, message: `Row ${i}: missing required value(s).` });
      continue;
    }

    rows.push({ title, description: desc, type });
  }

  return { rows, errors };
}

export default function BulkUploadFactsPage() {
  const [parsedRows, setParsedRows] = React.useState<ParsedFact[]>([]);
  const [parseErrors, setParseErrors] = React.useState<RowError[]>([]);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosPrivate.post(API_ROUTES.FACTS.BULK_UPLOAD, {
        facts: parsedRows,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(`${parsedRows.length} facts uploaded successfully`);
      setParsedRows([]);
      setParseErrors([]);
      setFileName(null);
    },
    onError: () => {
      toast.error("Bulk upload failed. Please try again.");
    },
  });

  function handleFile(file: File) {
    if (!file.name.endsWith(".csv")) {
      toast.error("Only CSV files are supported.");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { rows, errors } = parseCSV(text);
      setParsedRows(rows);
      setParseErrors(errors);
      if (rows.length > 0) toast.success(`${rows.length} row(s) parsed successfully`);
    };
    reader.readAsText(file);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_facts.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function clearFile() {
    setParsedRows([]);
    setParseErrors([]);
    setFileName(null);
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl">Bulk Upload Facts</h1>
        <p className="text-muted-foreground mt-1">
          Upload a CSV file to add multiple facts at once.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="outline" size="sm" onClick={downloadSample} className="flex items-center gap-2">
          <Download size={14} />
          Download Sample CSV
        </Button>
      </div>

      {/* CSV Format Info */}
      <div className="rounded-md border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
        <p className="font-medium text-foreground mb-1">Required CSV columns:</p>
        <code className="text-xs">title, description, type</code>
        <p className="mt-1 text-xs">Valid types: <code>gut</code>, <code>nutrition</code>, <code>sleep</code></p>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 cursor-pointer transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/30"
        }`}
      >
        <Upload size={32} className="text-muted-foreground" />
        <div className="text-center">
          <p className="font-medium">Drop your CSV file here</p>
          <p className="text-sm text-muted-foreground">or click to browse</p>
        </div>
        {fileName && (
          <div className="flex items-center gap-2 text-sm text-primary font-medium">
            <CheckCircle2 size={14} />
            {fileName}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Parse Errors */}
      {parseErrors.length > 0 && (
        <div className="space-y-1">
          {parseErrors.map((err, i) => (
            <div key={i} className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              {err.message}
            </div>
          ))}
        </div>
      )}

      {/* Preview Table */}
      {parsedRows.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{parsedRows.length} row(s) ready to upload</p>
            <Button variant="ghost" size="sm" onClick={clearFile} className="text-muted-foreground hover:text-destructive">
              <X size={14} className="mr-1" /> Clear
            </Button>
          </div>

          <div className="rounded-md border overflow-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground w-40">Title</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Description</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground w-28">Type</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 font-medium">{row.title}</td>
                    <td className="px-3 py-2 text-muted-foreground line-clamp-2">{row.description}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 capitalize">{row.type}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end">
            <Button
              onClick={() => uploadMutation.mutate()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-2"
            >
              {uploadMutation.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Upload size={16} />
              )}
              Upload {parsedRows.length} Facts
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
